import PubSub from 'pubsub-js'
import { CHAT_SOCKET_URL } from '@/shared/config/runtime'

let socket = null
let socketSerial = 0
let sessKey = null
let sessSalt = ''
let sessType = 'user'
let sessBranch = ''
let reconnectTimer = null
let reconnectCount = 0
let manualDisconnect = false

// 0 = keep reconnecting forever. Set NEXT_PUBLIC_CHAT_MAX_RECONNECTS to a
// positive number only if you want to stop after N retries.
const MAX_RECONNECTS = Number(process.env.NEXT_PUBLIC_CHAT_MAX_RECONNECTS || 0)
const BASE_DELAY_MS = Number(process.env.NEXT_PUBLIC_CHAT_RECONNECT_BASE_MS || 1500)
const MAX_DELAY_MS = Number(process.env.NEXT_PUBLIC_CHAT_RECONNECT_MAX_MS || 15000)

const pub = (topic, data) => PubSub.publish(topic, data)

function buildUrl(key, salt = '', type = 'user', branch = '') {
  const qs = new URLSearchParams({ sesskey: key, salt, type })
  // Always pass branch/family when available. Normal users also need this
  // so the WS profile can enforce same Branch/Family private-chat rules.
  if (branch) qs.set('branch', branch)
  return `${CHAT_SOCKET_URL}?${qs.toString()}`
}

function normalizeType(type = 'user') {
  return String(type || 'user').trim().toLowerCase()
}

function normalizeBranch(branch = '') {
  return branch == null ? '' : String(branch).trim().toUpperCase()
}

function sameConnectionParams(key, salt, type, branch) {
  return sessKey === key
    && sessSalt === (salt || '')
    && sessType === normalizeType(type)
    && sessBranch === normalizeBranch(branch)
}

export function isChatSocketConnected() {
  return typeof WebSocket !== 'undefined' && socket?.readyState === WebSocket.OPEN
}

export function getChatSocketState() {
  if (typeof WebSocket === 'undefined' || !socket) return 'CLOSED'
  switch (socket.readyState) {
    case WebSocket.CONNECTING: return 'CONNECTING'
    case WebSocket.OPEN: return 'OPEN'
    case WebSocket.CLOSING: return 'CLOSING'
    case WebSocket.CLOSED: return 'CLOSED'
    default: return 'UNKNOWN'
  }
}

function sendRaw(payload) {
  if (!isChatSocketConnected()) return false
  socket.send(JSON.stringify(payload))
  return true
}

export function waitForChatSocketOpen(timeoutMs = 4000) {
  if (typeof window === 'undefined') return Promise.resolve(false)
  if (isChatSocketConnected()) return Promise.resolve(true)
  if ((!socket || socket.readyState === WebSocket.CLOSED) && sessKey) {
    connectChatSocket(sessKey, sessSalt, sessType, sessBranch)
  }
  return new Promise(resolve => {
    const started = Date.now()
    const t = setInterval(() => {
      if (isChatSocketConnected()) {
        clearInterval(t)
        resolve(true)
      } else if (Date.now() - started >= timeoutMs) {
        clearInterval(t)
        resolve(false)
      }
    }, 80)
  })
}

async function sendRawWhenOpen(payload, timeoutMs = 4000) {
  const ok = await waitForChatSocketOpen(timeoutMs)
  if (!ok) return false
  return sendRaw(payload)
}


function isAuthFailureClose(code, reason = '') {
  const text = String(reason || '').toLowerCase()
  return Number(code) === 4001 || text.includes('session_expired') || text.includes('session expired') || text.includes('auth_rejected') || text.includes('invalid sesskey') || text.includes('missing sesskey') || text.includes('malformed sesskey')
}

function normalizeAuthFailureReason(reason = '') {
  const text = String(reason || '')
  if (/session[_\s-]*expired/i.test(text) || /login again/i.test(text)) return 'Your session has expired. Please login again.'
  if (/missing sesskey/i.test(text)) return 'Login session missing. Please login again.'
  if (/invalid sesskey|malformed sesskey|auth_rejected|unable to resolve/i.test(text)) return 'Login session is invalid. Please login again.'
  return 'Your login session ended. Please login again.'
}

function forceSessionLogout(reason = '') {
  manualDisconnect = true
  clearReconnectTimer()
  const message = normalizeAuthFailureReason(reason)

  pub('chat:auth_expired', { message, reason })

  if (typeof window === 'undefined') return

  try {
    window.dispatchEvent(new CustomEvent('kalp:session-expired', { detail: { message, reason } }))
  } catch (_) {}

  try { window.localStorage.setItem('KalpSessionExpired', String(Date.now())) } catch (_) {}
  try { document.cookie = 'KalpUser=; Max-Age=-99999999; path=/;' } catch (_) {}

  const currentPath = window.location?.pathname || ''
  if (!currentPath.startsWith('/login')) {
    window.location.replace('/login?expired=1')
  }
}

function onMessage({ data }) {
  let event
  try { event = JSON.parse(data) } catch { return }

  switch (event.type) {
    case 'bootstrap_v4':
      pub('chat:bootstrap', event)
      break
    case 'users_v4':
      pub('chat:users', { users: event.users || [], groupCandidates: event.groupCandidates || [] })
      break
    case 'group_candidate_search_v4':
      pub('chat:group_candidate_search', { candidates: event.candidates || [], query: event.query || '', requestId: event.requestId || null })
      break
    case 'private_contact_search_v4':
      pub('chat:private_contact_search', { contacts: event.contacts || [], query: event.query || '', requestId: event.requestId || null })
      break
    case 'conversations_v4':
      pub('chat:conversations', { conversations: event.conversations || [] })
      break
    case 'conversation_opened_v4':
      pub('chat:opened', event)
      break
    case 'history_v4':
      pub('chat:history', event)
      break
    case 'message_v4':
      pub('chat:message', { message: event.message })
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('kalp-chat:new_message', { detail: event }))
      }
      break
    case 'message_updated_v4':
      pub('chat:message_updated', { message: event.message })
      break
    case 'message_deleted_v4':
      pub('chat:message_deleted', event)
      break
    case 'forward_done_v4':
      pub('chat:forward_done', event)
      break
    case 'search_results_v4':
      pub('chat:search_results', event)
      break
    case 'groups_manage_list_v4':
      pub('chat:groups_manage_list', event)
      break
    case 'broadcast_done_v4':
      pub('chat:broadcast_done', event)
      break
    case 'scheduled_broadcasts_list_v4':
      pub('chat:scheduled_broadcasts_list', event)
      break
    case 'scheduled_broadcast_cancelled_v4':
      pub('chat:scheduled_broadcast_cancelled', event)
      break
    case 'scheduled_message_saved_v4':
      pub('chat:scheduled_message_saved', event)
      break
    case 'scheduled_messages_list_v4':
      pub('chat:scheduled_messages_list', event)
      break
    case 'scheduled_message_cancelled_v4':
      pub('chat:scheduled_message_cancelled', event)
      break
    case 'message_status_v4':
      pub('chat:message_status', event)
      break
    case 'message_reaction_v4':
      pub('chat:message_reaction', event)
      break
    case 'audit_list_v4':
      pub('chat:audit_list', event)
      break
    case 'user_status_list_v4':
      pub('chat:user_status_list', event)
      break
    case 'health_v4':
      pub('chat:health', event)
      break
    case 'permission_check_v4':
      pub('chat:permission_check', event)
      break
    case 'typing_v4':
      pub('chat:typing', event)
      break
    case 'presence_v4':
      pub('chat:presence', event)
      break
    case 'error_v4':
    case 'error': {
      const code = event.code || ''
      const message = event.message || ''
      pub('chat:error', { message, code })
      console.warn('[ChatSocket]', code, message)
      if (isAuthFailureClose(code === 'SESSION_EXPIRED' || code === 'AUTH_REJECTED' ? 4001 : 0, message || code)) {
        forceSessionLogout(message || code)
      }
      break
    }
    // Call signalling. Republished under one topic so the call controller can
    // drive its state machine from a single subscription.
    case 'call_config_v4':
    case 'call_incoming_v4':
    case 'call_ringing_v4':
    case 'call_accepted_v4':
    case 'call_answered_v4':
    case 'call_ice_v4':
    case 'call_ended_v4':
    case 'call_unavailable_v4':
    // A fan-out resolving is a call-state change like any other: the caller has
    // to tear down the legs that lost, so it rides the same topic.
    case 'call_fanout_resolved_v4':
    case 'support_call_targets_v4':
    // A late agent joining adds a leg to a fan-out already in flight, so the
    // caller handles it exactly where it handles the original target list.
    case 'support_call_add_target_v4':
      pub('chat:call', event)
      break
    case 'support_call_join_result_v4':
      pub('chat:call_join_result', event)
      break
    case 'support_desks_v4':
      pub('chat:support_desks', { desks: event.desks || [], canManage: Boolean(event.canManage) })
      break
    case 'support_desk_saved_v4':
      pub('chat:support_desk_saved', { desk: event.desk || null })
      break
    case 'support_agent_candidates_v4':
      pub('chat:support_agent_candidates', { candidates: event.candidates || [] })
      break
    case 'support_holidays_v4':
      pub('chat:support_holidays', { holidays: event.holidays || [] })
      break
    case 'push_config_v4':
      pub('chat:push_config', {
        enabled: Boolean(event.enabled),
        publicKey: event.publicKey || '',
        subscriptions: Number(event.subscriptions) || 0,
      })
      break
    case 'push_subscribed_v4':
      pub('chat:push_subscribed', { subscriptions: Number(event.subscriptions) || 0 })
      break
    case 'call_history_v4':
      pub('chat:call_history', { history: event.history || [] })
      break
    case 'pong_v4':
    case 'pong':
      break
    default:
      break
  }
}

function clearReconnectTimer() {
  if (!reconnectTimer) return
  clearTimeout(reconnectTimer)
  reconnectTimer = null
}

function scheduleReconnect(closeCode, reason) {
  if (!sessKey || manualDisconnect) return

  if (MAX_RECONNECTS > 0 && reconnectCount >= MAX_RECONNECTS) {
    pub('chat:reconnecting', {
      attempt: reconnectCount,
      delayMs: null,
      code: closeCode,
      reason,
      stopped: true,
    })
    return
  }

  const delay = Math.min(MAX_DELAY_MS, Math.round(BASE_DELAY_MS * Math.pow(1.5, reconnectCount)))
  reconnectCount += 1
  pub('chat:reconnecting', {
    attempt: reconnectCount,
    delayMs: delay,
    code: closeCode,
    reason,
    stopped: false,
  })

  clearReconnectTimer()
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null
    connectChatSocket(sessKey, sessSalt, sessType, sessBranch, { force: true })
  }, delay)
}

export function connectChatSocket(key, salt = '', type = 'user', branch = '', options = {}) {
  if (typeof window === 'undefined') return null
  if (!key) {
    pub('chat:error', { message: 'Session key missing.', code: 'MISSING_SESSKEY' })
    return null
  }

  const safeType = normalizeType(type)
  const safeBranch = normalizeBranch(branch)
  if (safeType === 'branch' && !safeBranch) {
    pub('chat:error', { message: 'Branch/Family code missing.', code: 'MISSING_BRANCH' })
    return null
  }

  const paramsUnchanged = sameConnectionParams(key, salt, safeType, safeBranch)
  sessKey = key
  sessSalt = salt || ''
  sessType = safeType
  sessBranch = safeBranch
  manualDisconnect = false

  if (!options.force && paramsUnchanged && socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
    return socket
  }

  if (options.force && socket && socket.readyState === WebSocket.CONNECTING) return socket

  const currentSerial = ++socketSerial
  const ws = new WebSocket(buildUrl(key, sessSalt, safeType, safeBranch))
  socket = ws
  pub('chat:connecting', { attempt: reconnectCount + 1 })

  ws.onopen = () => {
    if (socket !== ws || currentSerial !== socketSerial) return
    reconnectCount = 0
    clearReconnectTimer()
    pub('chat:connected', {})
  }

  ws.onmessage = event => {
    if (socket !== ws || currentSerial !== socketSerial) return
    onMessage(event)
  }

  ws.onclose = evt => {
    // Important: ignore stale socket close events. During reconnect or React
    // remount, an old socket can close after a newer socket is already active.
    // Without this guard the old close clears the active socket reference and
    // the UI gets stuck in reconnect/error state.
    if (socket !== ws || currentSerial !== socketSerial) return

    socket = null
    pub('chat:closed', { code: evt.code, reason: evt.reason })

    if (isAuthFailureClose(evt.code, evt.reason)) {
      forceSessionLogout(evt.reason)
      return
    }

    if (manualDisconnect || evt.code === 1000) return

    // 4002 = the server handed this account to a NEWER connection (another tab,
    // another device, or a fresh socket from this same client). Do NOT
    // reconnect here: if we reconnect we supersede that newer connection, it
    // reconnects and supersedes us, and the two sides ping-pong forever
    // (an infinite reconnect war, ~every reconnect delay). Yield instead and
    // let the newest connection own the account. The user can reclaim this tab
    // via resetChatSocketReconnect() (the "Retry" action).
    if (evt.code === 4002) {
      manualDisconnect = true
      clearReconnectTimer()
      pub('chat:superseded', { code: evt.code, reason: evt.reason })
      return
    }

    scheduleReconnect(evt.code, evt.reason)
  }

  ws.onerror = () => {
    if (socket !== ws || currentSerial !== socketSerial) return
    pub('chat:socket_error', {})
  }

  return ws
}

export function disconnectChatSocket() {
  manualDisconnect = true
  clearReconnectTimer()
  const ws = socket
  socket = null
  socketSerial += 1
  if (ws) {
    try { ws.close(1000, 'User logout') } catch (_) {}
  }
}

export function resetChatSocketReconnect() {
  reconnectCount = 0
  manualDisconnect = false
  clearReconnectTimer()
  if (sessKey) connectChatSocket(sessKey, sessSalt, sessType, sessBranch, { force: true })
}

export function getChatSocket() {
  return socket
}

export const chatActions = {
  isConnected: () => isChatSocketConnected(),
  socketState: () => getChatSocketState(),
  waitUntilConnected: (ms = 4000) => waitForChatSocketOpen(ms),
  reconnect: () => resetChatSocketReconnect(),

  bootstrap: () => sendRaw({ type: 'bootstrap' }),
  bootstrapAsync: () => sendRawWhenOpen({ type: 'bootstrap' }),
  getUsers: () => sendRaw({ type: 'get_users' }),
  getConversations: () => sendRaw({ type: 'get_conversations' }),
  searchGroupCandidates: (payload = {}) => sendRaw({ type: 'search_group_candidates_v4', ...payload }),
  searchGroupCandidatesAsync: (payload = {}) => sendRawWhenOpen({ type: 'search_group_candidates_v4', ...payload }),
  searchPrivateContacts: (payload = {}) => sendRaw({ type: 'search_private_contacts_v4', ...payload }),
  searchPrivateContactsAsync: (payload = {}) => sendRawWhenOpen({ type: 'search_private_contacts_v4', ...payload }),

  openPrivate: (target) => {
    const payload = typeof target === 'object' && target !== null
      ? { targetCode: target.code || target.targetCode, targetProfile: target }
      : { targetCode: target }
    return sendRaw({ type: 'open_private', ...payload })
  },
  openPrivateAsync: (target) => {
    const payload = typeof target === 'object' && target !== null
      ? { targetCode: target.code || target.targetCode, targetProfile: target }
      : { targetCode: target }
    return sendRawWhenOpen({ type: 'open_private', ...payload })
  },
  openGroup: (conversationId) => sendRaw({ type: 'open_group', conversationId }),
  openGroupAsync: (conversationId) => sendRawWhenOpen({ type: 'open_group', conversationId }),
  createGroup: (title, memberCodes) => sendRaw({ type: 'create_group', title, memberCodes }),
  createGroupAsync: (title, memberCodes) => sendRawWhenOpen({ type: 'create_group', title, memberCodes }),
  loadManagedGroups: () => sendRaw({ type: 'groups_manage_list_v4' }),
  renameGroup: (conversationId, title) => sendRaw({ type: 'group_rename_v4', conversationId, title }),
  addGroupMembers: (conversationId, memberCodes) => sendRaw({ type: 'group_add_members_v4', conversationId, memberCodes }),
  removeGroupMember: (conversationId, memberCode) => sendRaw({ type: 'group_remove_member_v4', conversationId, memberCode }),
  deactivateGroup: (conversationId) => sendRaw({ type: 'group_deactivate_v4', conversationId }),
  broadcast: (payload = {}) => sendRaw({ type: 'broadcast_v4', ...payload }),
  loadScheduledBroadcasts: (payload = {}) => sendRaw({ type: 'scheduled_broadcasts_list_v4', ...payload }),
  cancelScheduledBroadcast: (scheduledId) => sendRaw({ type: 'cancel_scheduled_broadcast_v4', scheduledId }),
  scheduleMessage: (conversationId, content, opts = {}) => sendRaw({ type: 'schedule_message_v4', conversationId, content, ...opts }),
  scheduleMessageAsync: (conversationId, content, opts = {}) => sendRawWhenOpen({ type: 'schedule_message_v4', conversationId, content, ...opts }),
  loadScheduledMessages: (payload = {}) => sendRaw({ type: 'scheduled_messages_list_v4', ...payload }),
  cancelScheduledMessage: (scheduledId) => sendRaw({ type: 'cancel_scheduled_message_v4', scheduledId }),
  toggleReaction: (messageId, emoji) => sendRaw({ type: 'reaction_toggle_v4', messageId, emoji }),
  loadAudit: (payload = {}) => sendRaw({ type: 'audit_list_v4', ...payload }),
  loadUserStatuses: () => sendRaw({ type: 'user_status_list_v4' }),
  updateUserStatus: (targetCode, status) => sendRaw({ type: 'user_status_update_v4', targetCode, status }),
  loadHealth: () => sendRaw({ type: 'health_v4' }),
  checkPermission: (fromCode, toCode) => sendRaw({ type: 'permission_check_v4', fromCode, toCode }),

  sendMessage: (conversationId, content, opts = {}) => sendRaw({ type: 'send_message_v4', conversationId, content, ...opts }),
  sendMessageAsync: (conversationId, content, opts = {}) => sendRawWhenOpen({ type: 'send_message_v4', conversationId, content, ...opts }),
  editMessage: (messageId, content) => sendRaw({ type: 'edit_message_v4', messageId, content }),
  deleteMessage: (messageId, mode = 'for_me') => sendRaw({ type: 'delete_message_v4', messageId, mode }),
  hardDeleteMessage: (messageId) => sendRaw({ type: 'hard_delete_message_v4', messageId }),
  forwardMessage: (messageId, targetCodes = [], conversationIds = []) => sendRaw({ type: 'forward_message_v4', messageId, targetCodes, conversationIds }),
  searchMessages: (query, opts = {}) => sendRaw({ type: 'search_messages_v4', query, ...opts }),
  loadMore: (conversationId, before) => sendRaw({ type: 'load_more_v4', conversationId, before }),
  markRead: (conversationId, messageId) => sendRaw({ type: 'mark_read_v4', conversationId, messageId }),
  typingStart: (conversationId) => sendRaw({ type: 'typing_start_v4', conversationId }),
  typingStop: (conversationId) => sendRaw({ type: 'typing_stop_v4', conversationId }),
  ping: () => sendRaw({ type: 'ping_v4' }),

  // Voice calls — signalling only; the audio itself is peer-to-peer.
  callConfig: () => sendRaw({ type: 'call_config_v4' }),
  callStart: (conversationId, sdp, media = 'audio') => sendRaw({ type: 'call_start_v4', conversationId, sdp, media }),
  callAnswer: (callId, sdp) => sendRaw({ type: 'call_answer_v4', callId, sdp }),
  callIce: (callId, candidate) => sendRaw({ type: 'call_ice_v4', callId, candidate }),
  callReject: (callId) => sendRaw({ type: 'call_reject_v4', callId }),
  callEnd: (callId, reason = 'hangup') => sendRaw({ type: 'call_end_v4', callId, reason }),
  callHistory: (conversationId = null) => sendRaw({ type: 'call_history_v4', conversationId }),

  // Support desks. loadSupportDesks answers differently by role: a manager gets
  // the full roster to edit, everyone else gets names and availability only.
  loadSupportDesks: () => sendRaw({ type: 'support_desks_v4' }),
  loadSupportDesksAsync: () => sendRawWhenOpen({ type: 'support_desks_v4' }),
  // The pool a desk can be staffed from: admin accounts only, never clients.
  loadSupportAgentCandidates: (payload = {}) =>
    sendRaw({ type: 'support_agent_candidates_v4', ...payload }),
  loadSupportAgentCandidatesAsync: (payload = {}) =>
    sendRawWhenOpen({ type: 'support_agent_candidates_v4', ...payload }),
  saveSupportDeskAgents: (deskKey, agentCodes = []) =>
    sendRaw({ type: 'support_desk_agents_set_v4', deskKey, agentCodes }),
  updateSupportDesk: (deskKey, patch = {}) =>
    sendRaw({ type: 'support_desk_update_v4', deskKey, ...patch }),

  // Trading-day calendar. A trading day is Mon–Fri minus these dates; there is
  // no such calendar anywhere in the trading database to read instead.
  // Web push. The public key is fetched from the server so a key rotation
  // does not need a front-end redeploy.
  pushConfig: () => sendRaw({ type: 'push_config_v4' }),
  pushConfigAsync: () => sendRawWhenOpen({ type: 'push_config_v4' }),
  // These wait for the socket rather than using sendRaw. A subscription is
  // created once and then reused forever by the browser, so a send dropped
  // because the socket happened to be reconnecting would leave that browser
  // subscribed locally but unknown to the server — and nothing would ever try
  // again. Returns a promise resolving false if it could not be sent.
  pushSubscribe: (subscription, userAgent = '') =>
    sendRawWhenOpen({ type: 'push_subscribe_v4', subscription, userAgent }),
  pushUnsubscribe: endpoint => sendRawWhenOpen({ type: 'push_unsubscribe_v4', endpoint }),

  loadSupportHolidays: () => sendRaw({ type: 'support_holidays_v4' }),
  loadSupportHolidaysAsync: () => sendRawWhenOpen({ type: 'support_holidays_v4' }),
  addSupportHoliday: (date, label = '') => sendRaw({ type: 'support_holiday_add_v4', date, label }),
  removeSupportHoliday: date => sendRaw({ type: 'support_holiday_remove_v4', date }),
  // Phase one of a fan-out: ask who to ring. The offers follow as normal
  // callStart calls, one per returned target.
  supportCallStart: (deskKey) => sendRaw({ type: 'support_call_start_v4', deskKey }),
  // Queued rather than sent outright: this fires while the app is still cold —
  // the agent tapped Answer on a notification and the socket is mid-handshake.
  // A plain send would be dropped and the call lost for the sake of a few
  // hundred milliseconds. The window is generous because the ring lasts 45s.
  supportCallJoin: (fanoutId) => sendRawWhenOpen({ type: 'support_call_join_v4', fanoutId }, 15000),
  callStartTo: (conversationId, targetCode, sdp, opts = {}) =>
    sendRaw({ type: 'call_start_v4', conversationId, targetCode, sdp, media: 'audio', ...opts }),

  // Backward-compatible aliases used by older imports. New v4 UI does not use them.
  joinRoom: (userCode) => sendRaw({ type: 'open_private', targetCode: userCode }),
  joinRoomAsync: (userCode) => sendRawWhenOpen({ type: 'open_private', targetCode: userCode }),
  joinUserRoom: (targetUserId) => sendRaw({ type: 'open_private', targetCode: targetUserId }),
  joinUserRoomAsync: (targetUserId) => sendRawWhenOpen({ type: 'open_private', targetCode: targetUserId }),
}
